import * as z from "zod";
import { addTagReturnAction } from "./definitions/add-tag";
import { approveReturnAction } from "./definitions/approve";
import { closeReturnAction } from "./definitions/close";
import { flagReturnAction } from "./definitions/flag";
import { processReturnAction } from "./definitions/process";
import { rejectReturnAction } from "./definitions/reject";
import { resetReturnAction } from "./definitions/reset";
import { unflagReturnAction } from "./definitions/unflag";
import { ReturnActionDef } from "./types";

/**
 * The one list. Adding a return action is a definition file and a line here;
 * the id union, the payload types and every surface that offers actions all
 * derive from this object, so a new action cannot be half-registered.
 */
const RETURN_ACTIONS_ = {
  reset: resetReturnAction,
  process: processReturnAction,
  reject: rejectReturnAction,
  flag: flagReturnAction,
  unflag: unflagReturnAction,
  addTag: addTagReturnAction,
  close: closeReturnAction,
  approve: approveReturnAction,
};

export type ReturnActionId = keyof typeof RETURN_ACTIONS_;

/** `Object.keys` widens to string; the keys are this union by construction. */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const RETURN_ACTION_IDS = Object.keys(
  RETURN_ACTIONS_,
) as ReturnActionId[];

export const ReturnActionIdSchema = z.enum(RETURN_ACTION_IDS);

/**
 * A bound on one request's body and one modal's list, not on the run: the
 * workflow keeps its own concurrency window and can carry any length.
 */
export const MAX_BULK_RETURN_IDS = 500;

type PayloadOf<D> = D extends ReturnActionDef<infer P> ? P : never;

export type ReturnActionPayloads = {
  [K in ReturnActionId]: PayloadOf<(typeof RETURN_ACTIONS_)[K]>;
};

export type ReturnActionRegistry = {
  [K in ReturnActionId]: ReturnActionDef<ReturnActionPayloads[K]>;
};

/**
 * The same object, read through the mapped type. Indexing this with a type
 * parameter keeps a definition's payload tied to whatever consumes it, which a
 * bare `typeof` lookup would collapse into an unusable union.
 */
export const RETURN_ACTIONS: ReturnActionRegistry = RETURN_ACTIONS_;
